if (!customElements.get('jogging-configurator')) {
  customElements.define(
    'jogging-configurator',
    class JoggingConfigurator extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.form = null;
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        const configEl = this.querySelector('[data-jogging-config]');
        if (!configEl) return;

        this.config = JSON.parse(configEl.textContent);
        this.form = document.getElementById(this.dataset.formId);
        this.errorEl = this.querySelector('[data-config-error]');
        this.previewEl = this.querySelector('[data-pack-preview]');
        this.previewListEl = this.querySelector('[data-preview-list]');
        this.stoneDetailEl = this.querySelector('[data-stone-detail]');
        this.stoneDetailInner = this.querySelector('[data-stone-detail-inner]');

        this.bindEvents();
        this.refresh();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      bindEvents() {
        this.querySelectorAll('[data-belt-color], [data-stone]').forEach((input) => {
          input.addEventListener('change', () => {
            this.refresh();
            this.clearError();
          });
        });

        this.form?.querySelector('[name="id"]')?.addEventListener('change', () => {
          this.refresh();
          this.clearError();
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.refresh();
          });
        }

        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this), true);
        }
      }

      getSubmitButton() {
        return this.closest('product-info')?.querySelector('product-form [type="submit"]');
      }

      getSelectedBeltColor() {
        const input = this.querySelector('[data-belt-color]:checked:not(:disabled)');
        if (!input) return null;
        const color = this.config.beltColors.find((c) => c.id === input.value) || null;
        if (!color || color.inStock === false) return null;
        return color;
      }

      getSelectedStone() {
        const input = this.querySelector('[data-stone]:checked:not(:disabled)');
        if (!input) return null;
        const stone = this.config.stones.find((s) => s.id === input.value) || null;
        if (!stone || stone.inStock === false) return null;
        return {
          ...stone,
          description: stone.description || input.dataset.description || '',
          origin: input.dataset.origin || stone.origin || '',
          tagColor: input.dataset.tagColor || stone.tagColor || '#E8E0D0',
        };
      }

      getMainVariantId() {
        const raw = this.form?.querySelector('[name="id"]')?.value;
        const id = Number(String(raw || '').trim());
        return Number.isFinite(id) && id > 0 ? id : null;
      }

      getSelectedSizeVariant() {
        const id = this.getMainVariantId();
        if (!id) return null;
        return (this.config.sizeVariants || []).find((variant) => variant.id === id) || null;
      }

      isSizeInStock() {
        const variant = this.getSelectedSizeVariant();
        if (!variant) return false;
        if (variant.available === false) return false;
        if (variant.inventoryManagement === 'shopify' && Number(variant.inventoryQuantity) <= 0) {
          return false;
        }
        return true;
      }

      isComplete() {
        return !!this.getSelectedBeltColor() && !!this.getSelectedStone() && this.isSizeInStock();
      }

      setProperty(name, value) {
        const input = this.querySelector(`[data-property="${name}"]`);
        if (!input) return;
        input.value = value || '';
      }

      updateProperties() {
        const color = this.getSelectedBeltColor();
        const stone = this.getSelectedStone();
        this.setProperty('Ceinture couleur', color?.name || '');
        this.setProperty('Pierre', stone?.name || '');
      }

      updateProgress() {
        const color = !!this.getSelectedBeltColor();
        const stone = !!this.getSelectedStone();
        const colorSection = this.querySelector('[data-pack-section="color"]');
        const stoneSection = this.querySelector('[data-pack-section="stone"]');
        colorSection?.classList.toggle('is-done', color);
        stoneSection?.classList.toggle('is-done', stone);
        if (color) colorSection?.classList.remove('is-needed');
        if (stone) stoneSection?.classList.remove('is-needed');
      }

      updatePreview() {
        if (!this.previewEl || !this.previewListEl) return;

        const color = this.getSelectedBeltColor();
        const stone = this.getSelectedStone();

        if (!color) {
          this.previewEl.hidden = true;
          this.previewListEl.innerHTML = '';
          return;
        }

        const items = [
          `<span class="jogging-pack__preview-inline">${this.config.joggingLabel}</span><span class="jogging-pack__preview-sep">·</span><span class="jogging-pack__preview-inline">${this.config.officeBeltLabel}</span>`,
          `2e ceinture — ${color.name}`,
        ];
        if (stone?.name) items.push(`Pierre — ${stone.name}`);

        this.previewListEl.innerHTML = items
          .map((item) => `<li class="jogging-pack__preview-item">${item}</li>`)
          .join('');
        this.previewEl.hidden = false;
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      formatStoneDescription(description) {
        return this.escapeHtml(
          String(description || '')
            .replace(/\.([A-Za-zÀ-ÖØ-öø-ÿ])/g, '. $1')
            .replace(/[\r\n]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
        );
      }

      updateStoneDetail() {
        if (!this.stoneDetailEl || !this.stoneDetailInner) return;

        const stone = this.getSelectedStone();
        if (!stone?.description) {
          this.stoneDetailEl.hidden = true;
          return;
        }

        this.stoneDetailInner.style.background = stone.tagColor || '#E8E0D0';
        this.stoneDetailInner.innerHTML = `
          <p class="jogging-pack__stone-detail-name">${this.escapeHtml(stone.name)}</p>
          <div class="jogging-pack__stone-detail-body">${this.formatStoneDescription(stone.description)}</div>
          ${stone.origin ? `<span class="jogging-pack__stone-detail-origin">${this.escapeHtml(stone.origin)}</span>` : ''}
        `;
        this.stoneDetailEl.hidden = false;
      }

      captureAddLabel() {
        if (this.addToCartLabel) return;
        const text = this.getSubmitButton()?.querySelector('span')?.textContent?.trim();
        if (!text) return;
        const blocked = [window.variantStrings?.soldOut, window.variantStrings?.unavailable].filter(Boolean);
        if (blocked.includes(text)) return;
        this.addToCartLabel = text;
      }

      missingChoices() {
        const missing = [];
        if (!this.getSelectedBeltColor()) missing.push('color');
        if (!this.getSelectedStone()) missing.push('stone');
        return missing;
      }

      promptLabel() {
        const labels = this.config.labels || {};
        const missing = this.missingChoices();
        if (missing.includes('color') && missing.includes('stone')) {
          return labels.promptBoth || 'Choisissez votre 2e ceinture et votre pierre';
        }
        if (missing.includes('color')) return labels.promptBelt || 'Choisissez votre 2e ceinture';
        if (missing.includes('stone')) return labels.promptStone || 'Choisissez votre pierre';
        return this.addToCartLabel;
      }

      setButtonLabel(text) {
        const span = this.getSubmitButton()?.querySelector('span');
        if (span && text) span.textContent = text;
      }

      updateBuyButton() {
        const button = this.getSubmitButton();
        if (!button) return;
        this.captureAddLabel();

        const sizeOk = this.isSizeInStock();
        const missing = this.missingChoices();
        const canPrompt = sizeOk && missing.length > 0;

        if (!sizeOk) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('jogging-pack__buy-disabled');
          button.classList.remove('jogging-pack__buy-prompt');
          if (this.getMainVariantId()) this.setButtonLabel(window.variantStrings?.soldOut);
          return;
        }

        button.removeAttribute('disabled');
        button.setAttribute('aria-disabled', 'false');
        button.classList.remove('jogging-pack__buy-disabled');
        button.classList.toggle('jogging-pack__buy-prompt', canPrompt);
        this.setButtonLabel(canPrompt ? this.promptLabel() : this.addToCartLabel);
      }

      updateSizeMessage() {
        const msg = this.closest('product-info')?.querySelector('[data-jogging-size-msg]');
        if (!msg) return;
        const outOfStock = !this.isSizeInStock();
        msg.hidden = !outOfStock;
        if (outOfStock && this.config?.labels?.errorSizeOos) {
          msg.textContent = this.config.labels.errorSizeOos;
        }
      }

      refresh() {
        this.updateProperties();
        this.updateProgress();
        this.updatePreview();
        this.updateStoneDetail();
        this.updateBuyButton();
        this.updateSizeMessage();
        this.classList.toggle('is-complete', this.isComplete());
      }

      validate() {
        if (!this.getMainVariantId()) {
          this.showError(this.config.labels.errorSize);
          return false;
        }
        if (!this.isSizeInStock()) {
          this.showError(this.config.labels.errorSizeOos);
          return false;
        }
        const missing = this.missingChoices();
        if (!missing.length) return true;

        const labels = this.config.labels || {};
        const beltUnavailable = missing.includes('color') && !(this.config.beltColors || []).some((color) => color.inStock !== false);
        const stoneUnavailable = missing.includes('stone') && !(this.config.stones || []).some((stone) => stone.inStock !== false);
        if (beltUnavailable || stoneUnavailable) {
          this.showError(labels.soldOut);
          return false;
        }

        let message = labels.noticeBoth;
        if (missing.length === 1 && missing[0] === 'color') message = labels.noticeBelt || labels.errorBeltColor;
        if (missing.length === 1 && missing[0] === 'stone') message = labels.noticeStone || labels.errorStone;
        this.showError(message || labels.errorGeneric);
        this.highlightMissing(missing);
        return false;
      }

      highlightMissing(missing) {
        missing.forEach((key) => {
          const section = this.querySelector(`[data-pack-section="${key}"]`);
          if (!section) return;
          section.classList.remove('is-needed');
          void section.offsetWidth;
          section.classList.add('is-needed');
        });
        const first = this.querySelector(`[data-pack-section="${missing[0]}"]`);
        const top = first?.getBoundingClientRect().top ?? 0;
        if (first && (top < 80 || top > window.innerHeight * 0.55)) {
          first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      handleSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.validate()) return;
        this.addPackToCart();
      }

      addPackToCart() {
        const productForm = this.closest('product-info')?.querySelector('product-form');
        const button = this.getSubmitButton();
        const spinner = productForm?.querySelector('.loading__spinner');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const belt = this.getSelectedBeltColor();
        const stone = this.getSelectedStone();

        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');
        spinner?.classList.remove('hidden');

        const body = {
          items: [
            {
              id: this.getMainVariantId(),
              quantity: 1,
              properties: {
                'Ceinture couleur': belt.name,
                Pierre: stone.name,
              },
            },
          ],
        };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || this.config.labels.errorGeneric);
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch(() => {
            this.showError(this.config.labels.errorGeneric);
          })
          .finally(() => {
            button?.classList.remove('loading');
            spinner?.classList.add('hidden');
            this.updateBuyButton();
          });
      }
    }
  );
}
